// components/patient/Login.jsx
import React, { useState } from 'react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { DB } from '../../utils/db';
import { authService } from '../../services/authService';

export function Login({ onNavigate, onLogin }) {
  const { brand } = useBrand();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await authService.login(email.trim().toLowerCase(), password);
      const apiUser = result.user;
      const oldUserId = await DB.get(`email:${email.trim().toLowerCase()}`);
      if (oldUserId && oldUserId !== apiUser.id && oldUserId !== String(apiUser.id)) {
        const keys = ['d2p_prescriptions', 'd2p_orders', 'd2p_messages'];
        for (const key of keys) {
          const items = await DB.get(key);
          if (Array.isArray(items)) {
            let changed = false;
            for (const item of items) {
              if (item.userId === oldUserId) {
                item.userId = String(apiUser.id);
                changed = true;
              }
            }
            if (changed) await DB.set(key, items);
          }
        }
        await DB.del(`user:${oldUserId}`);
      }
      await DB.set(`user:${apiUser.id}`, apiUser);
      await DB.set(`email:${email.trim().toLowerCase()}`, apiUser.id);
      await DB.set('d2p_session', apiUser.id);
      toast.addToast('Welcome back!');
      onLogin(apiUser);
    } catch (apiError) {
      console.warn('API login failed, trying localStorage fallback:', apiError.message);
      try {
        const userId = await DB.get(`email:${email.trim().toLowerCase()}`);
        if (!userId) {
          toast.addToast('Invalid email or password', 'error');
          setLoading(false);
          return;
        }
        const user = await DB.get(`user:${userId}`);
        if (!user || user.password !== password) {
          toast.addToast('Invalid email or password', 'error');
          setLoading(false);
          return;
        }
        await DB.set('d2p_session', userId);
        toast.addToast('Welcome back!');
        onLogin(user);
      } catch (localError) {
        toast.addToast('Login failed. Please try again.', 'error');
        setLoading(false);
      }
    }
  };
  
  return (
    <div style={{
      minHeight: '100vh',
      background: '#F9FAFB',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <Card style={{ width: '100%', maxWidth: 450 }}>
        <div style={{
          textAlign: 'center',
          marginBottom: 32
        }}>
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={brand.name}
              style={{ height: 56, objectFit: 'contain', margin: '0 auto 16px', display: 'block' }}
            />
          ) : (
            <div style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              background: brand.c.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M12 2L4 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-8-4z" />
              </svg>
            </div>
          )}
          <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
            Welcome Back
          </h2>
          <p style={{ color: '#6B7280' }}>
            Sign in to your {brand.name} account
          </p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <Input
            label="Email Address"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              cursor: 'pointer'
            }}>
              <input type="checkbox" />
              Remember me
            </label>
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: brand.c.accent,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Forgot password?
            </button>
          </div>
          
          <Button type="submit" fullWidth disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        
        <div style={{
          marginTop: 24,
          padding: 16,
          background: '#EFF6FF',
          borderRadius: 8,
          fontSize: 13
        }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: '#1E40AF' }}>
            Demo Account
          </div>
          <div style={{ color: '#3B82F6', fontFamily: 'monospace' }}>
            Email: sarah.mitchell@email.com<br />
            Password: demo123
          </div>
        </div>
        
        <div style={{
          marginTop: 24,
          paddingTop: 24,
          borderTop: '1px solid #E5E7EB',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: 14, color: '#6B7280' }}>
            Don't have an account?{' '}
            <button
              onClick={() => onNavigate('register')}
              style={{
                background: 'none',
                border: 'none',
                color: brand.c.accent,
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Sign up
            </button>
          </p>
        </div>
        
        <Button
          variant="ghost"
          fullWidth
          onClick={() => onNavigate('landing')}
          style={{ marginTop: 16 }}
        >
          ← Back to Home
        </Button>
      </Card>
    </div>
  );
}
